import React, { useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import {
  MapPin, Upload, Download, FileText, Trash2, Building2, Phone, Mail, Globe, Briefcase,
  Landmark, Hash, CalendarClock,
} from "lucide-react";

const DOC_CATEGORIES = [
  { value: "gst",           label: "GST Certificate" },
  { value: "pan",           label: "PAN Card" },
  { value: "cin",           label: "CIN / Incorporation Certificate" },
  { value: "incorporation", label: "MOA / AOA / Incorporation Papers" },
  { value: "kyc",           label: "KYC — Aadhaar / ID Proof" },
  { value: "agreement",     label: "Supply Agreement / MSA" },
  { value: "purchase_order", label: "Purchase Order" },
  { value: "other",         label: "Other" },
];

const CAT_LABEL = Object.fromEntries(DOC_CATEGORIES.map((c) => [c.value, c.label]));

export default function CustomerDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDeleteDoc = ["admin", "accounts", "manager"].includes(user?.role);
  const canUpload = ["admin", "sales", "accounts", "manager"].includes(user?.role);

  const { data: cust } = useQuery({
    queryKey: ["customer", id],
    queryFn: () => api.get(`/customers/${id}`).then((r) => r.data),
  });
  const { data: orders } = useQuery({
    queryKey: ["customer-orders", id],
    queryFn: () => api.get(`/orders?customer_id=${id}`).then((r) => r.data.items),
    enabled: !!id,
  });
  const { data: invData } = useQuery({
    queryKey: ["customer-invoices", id],
    queryFn: () => api.get(`/customers/${id}/invoices`).then((r) => r.data),
    enabled: !!id,
  });
  const { data: ledger } = useQuery({
    queryKey: ["ledger", id],
    queryFn: () => api.get(`/customers/${id}/ledger`).then((r) => r.data),
    enabled: !!id,
  });

  if (!cust) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const invoices = invData?.invoices || [];
  const invTotals = invData?.totals || { count: 0, total: 0, paid: 0, due: 0 };

  return (
    <div className="space-y-6" data-testid="customer-detail">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{cust.code}</div>
          <h1 className="font-display text-3xl font-black tracking-tight">{cust.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge status={cust.kyc_status} />
            <span>· {cust.type}</span>
            {cust.industry && <span>· {cust.industry}</span>}
          </div>
        </div>
        <Link to="/app/customers">
          <Button variant="outline" size="sm">← All customers</Button>
        </Link>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPI label="Credit Limit" value={`₹${(cust.credit_limit || 0).toLocaleString("en-IN")}`} icon={Landmark} />
        <KPI label="Credit Days" value={`${cust.credit_days || 0}`} icon={CalendarClock} />
        <KPI label="Outstanding" value={`₹${(cust.outstanding || 0).toLocaleString("en-IN")}`} icon={FileText} highlight />
        <KPI label="Rate / Litre" value={`₹${cust.price_per_litre || 0}`} icon={Hash} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="w-full flex-wrap" data-testid="customer-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Invoices <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">{invTotals.count}</span>
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Statement / Ledger</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Documents <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">{(cust.documents || []).length}</span>
          </TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-sites">
            Sites <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">{(cust.sites || []).length}</span>
          </TabsTrigger>
        </TabsList>

        {/* ─────── Overview ─────── */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Briefcase size={16} /> Company</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Company" value={cust.name} />
                <Row label="Type" value={cust.type} />
                <Row label="Industry" value={cust.industry} />
                <Row label="Website" value={cust.website} icon={Globe} link={cust.website} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Phone size={16} /> Contact</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Contact person" value={`${cust.contact_person || "—"}${cust.contact_designation ? " · " + cust.contact_designation : ""}`} />
                <Row label="Phone" value={cust.phone} icon={Phone} />
                <Row label="Email" value={cust.email} icon={Mail} />
                <Row label="Alt phone" value={cust.alt_phone} />
                <Row label="Alt email" value={cust.alt_email} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Hash size={16} /> Statutory identifiers</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="GSTIN" value={cust.gstin} mono />
                <Row label="PAN" value={cust.pan} mono />
                <Row label="CIN" value={cust.cin} mono />
                <Row label="KYC Status" value={<StatusBadge status={cust.kyc_status} />} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Building2 size={16} /> Addresses</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Billing</div>
                  <div className="mt-1">{cust.billing_address || "—"}</div>
                  <div className="text-muted-foreground">{[cust.city, cust.state, cust.pincode].filter(Boolean).join(", ")}</div>
                </div>
                {cust.shipping_address && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Shipping</div>
                    <div className="mt-1">{cust.shipping_address}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle>Recent orders</CardTitle>
              <Link to={`/app/orders?customer=${cust.id}`}><Button variant="outline" size="sm">View all</Button></Link>
            </CardHeader>
            <CardContent className="p-0">
              <MiniOrdersTable orders={(orders || []).slice(0, 8)} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────── Invoices ─────── */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KPI label="Invoices" value={invTotals.count} />
            <KPI label="Billed" value={`₹${(invTotals.total || 0).toLocaleString("en-IN")}`} />
            <KPI label="Paid" value={`₹${(invTotals.paid || 0).toLocaleString("en-IN")}`} />
            <KPI label="Due" value={`₹${(invTotals.due || 0).toLocaleString("en-IN")}`} highlight />
          </div>
          <Card>
            <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="data-table w-full">
                <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3 text-right">Qty (L)</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Paid</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((iv) => (
                    <tr key={iv.id} className="border-b border-border/40 text-sm hover:bg-muted/30" data-testid={`inv-${iv.code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{iv.code}</td>
                      <td className="px-4 py-3 text-xs">{iv.invoice_date ? new Date(iv.invoice_date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{iv.order_code || iv.order_id?.slice(0, 8) || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">{iv.quantity_litres || 0}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{(iv.total || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{(iv.paid_amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3"><StatusBadge status={iv.status || (iv.paid_amount >= iv.total ? "paid" : "unpaid")} /></td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => downloadPdf(`/exports/invoice/${iv.id}/pdf`, `invoice_${iv.code}.pdf`)}
                          className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                          title="Download invoice PDF"
                          data-testid={`inv-pdf-${iv.code}`}
                        >
                          <Download size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No invoices yet.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────── Ledger / Statement ─────── */}
        <TabsContent value="ledger" className="space-y-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => downloadPdf(`/exports/customer/${cust.id}/statement/pdf`, `statement_${cust.code}.pdf`)}
              data-testid="btn-statement-pdf"
            >
              <Download size={14} className="mr-1.5" /> Download statement PDF
            </Button>
          </div>
          <Card>
            <CardHeader><CardTitle>Statement of account</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="data-table w-full">
                <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Ref</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-right">Debit</th>
                    <th className="px-4 py-3 text-right">Credit</th>
                    <th className="px-4 py-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(ledger?.entries || []).map((e) => (
                    <tr key={e.id} className="border-b border-border/40 text-sm">
                      <td className="px-4 py-3 text-xs">{e.date ? new Date(e.date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{e.ref_code || "—"}</td>
                      <td className="px-4 py-3 text-xs">{e.description || e.ref_type || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">{e.debit ? `₹${e.debit.toLocaleString("en-IN")}` : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">{e.credit ? `₹${e.credit.toLocaleString("en-IN")}` : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">₹{(e.balance || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                  {(!ledger?.entries || ledger.entries.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No entries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────── Documents ─────── */}
        <TabsContent value="documents" className="space-y-4">
          <DocumentsPanel
            customerId={cust.id}
            documents={cust.documents || []}
            canUpload={canUpload}
            canDelete={canDeleteDoc}
            onChanged={() => qc.invalidateQueries({ queryKey: ["customer", cust.id] })}
          />
        </TabsContent>

        {/* ─────── Sites ─────── */}
        <TabsContent value="sites" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Delivery sites ({cust.sites?.length || 0})</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {(cust.sites || []).map((s) => (
                <div key={s.id} className="rounded-lg border border-border/60 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold"><MapPin size={14} /> {s.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.address}, {s.city}, {s.state} - {s.pincode}</div>
                  <div className="mt-1 text-xs">{s.contact_name} · {s.contact_phone}</div>
                </div>
              ))}
              {(!cust.sites || cust.sites.length === 0) && (
                <div className="py-4 text-center text-sm text-muted-foreground md:col-span-2">No sites yet.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────── Documents panel ─────────── */
function DocumentsPanel({ customerId, documents, canUpload, canDelete, onChanged }) {
  const [openUpload, setOpenUpload] = useState(false);
  const [category, setCategory] = useState("gst");
  const [name, setName] = useState("");
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const doUpload = async () => {
    if (!file) return toast.error("Choose a file");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/customers/${customerId}/documents?name=${encodeURIComponent(name || file.name)}&category=${category}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document uploaded");
      setOpenUpload(false);
      setFile(null);
      setName("");
      if (fileInput.current) fileInput.current.value = "";
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (doc) => {
    try {
      await api.delete(`/customers/${customerId}/documents/${doc.id}`);
      toast.success("Document removed");
      setConfirmDelete(null);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-end">
        {canUpload && (
          <Button onClick={() => setOpenUpload(true)} data-testid="btn-upload-doc">
            <Upload size={14} className="mr-1.5" /> Upload document
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id} className="border-b border-border/40 text-sm hover:bg-muted/30" data-testid={`doc-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2"><FileText size={14} className="text-primary" /> {d.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                      {CAT_LABEL[d.category] || d.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{d.size_kb} KB</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : "—"}<br />
                    <span className="text-[10px]">by {d.uploaded_by}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={d.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Open"
                      >
                        <Download size={14} />
                      </a>
                      {canDelete && (
                        <button
                          onClick={() => setConfirmDelete(d)}
                          className="rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600"
                          title="Delete"
                          data-testid={`doc-del-${d.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {documents.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No documents yet. Upload GST, PAN, CIN certificates & agreements.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Display name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. GST Certificate FY24" />
            </div>
            <div>
              <Label>File</Label>
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
                data-testid="doc-file-input"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">Max 10 MB. PDF preferred.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUpload(false)}>Cancel</Button>
            <Button onClick={doUpload} disabled={uploading || !file} data-testid="btn-do-upload">
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.name}</b> will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && doDelete(confirmDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="doc-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ─────────── Helpers ─────────── */

async function downloadPdf(path, filename) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
}

const KPI = ({ label, value, highlight, icon: Icon }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      {Icon && <Icon size={14} className="text-muted-foreground" />}
    </div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-primary" : ""}`}>{value}</div>
  </div>
);

const Row = ({ label, value, mono, icon: Icon, link }) => (
  <div className="flex items-start gap-3">
    <div className="w-32 flex-shrink-0 text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`flex-1 ${mono ? "font-mono text-xs" : ""}`}>
      {value ? (
        link ? (
          <a href={link.startsWith("http") ? link : `https://${link}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline">
            {Icon && <Icon size={12} />} {value}
          </a>
        ) : Icon ? (
          <span className="flex items-center gap-1">{Icon && <Icon size={12} className="text-muted-foreground" />} {value}</span>
        ) : (
          value
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  </div>
);

const MiniOrdersTable = ({ orders }) => (
  <table className="data-table w-full">
    <thead className="border-b border-border/60 bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
      <tr>
        <th className="px-4 py-2">Code</th>
        <th className="px-4 py-2 text-right">Qty</th>
        <th className="px-4 py-2 text-right">Amount</th>
        <th className="px-4 py-2">Status</th>
      </tr>
    </thead>
    <tbody>
      {orders.map((o) => (
        <tr key={o.id} className="border-b border-border/40 text-sm">
          <td className="px-4 py-2 font-mono"><Link to={`/app/orders/${o.id}`} className="hover:text-primary">{o.code}</Link></td>
          <td className="px-4 py-2 text-right font-mono">{o.quantity_litres}L</td>
          <td className="px-4 py-2 text-right font-mono">₹{o.total_amount?.toLocaleString("en-IN")}</td>
          <td className="px-4 py-2"><StatusBadge status={o.status} /></td>
        </tr>
      ))}
      {(!orders || orders.length === 0) && (
        <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No orders yet.</td></tr>
      )}
    </tbody>
  </table>
);
